/**
 * User-wise wallet balances, and the ONE place a human can move one.
 *
 * The adjustment form demands a reason before it will submit, and the confirm
 * step restates the amount, the direction and the customer. That friction is the
 * feature: §11 requires every manual adjustment to carry a reason, an admin
 * identity and a before/after - so the UI must make an unexplained adjustment
 * impossible to submit, not merely discouraged.
 */
import { useCallback, useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'

import EmptyState from '../../components/shared/EmptyState'
import LoadErrorState from '../../components/common/LoadErrorState'
import {
  Card,
  Chip,
  Field,
  Modal,
  PillButton,
  SearchInput,
  SegmentedControl,
  TableHead,
  Td,
  TextArea,
  TextInput,
} from '../../features/b2b/components/primitives'
import { useColors } from '../../hooks/useColors'
import { emitToast } from '../../lib/toastBus'
import { userHasPermission } from '../../lib/permissions'
import { useAuthStore } from '../../store/authStore'
import { adjustWallet, formatINR, getWalletCustomers } from '../../services/walletApi'
import type { WalletCustomerRecord } from '../../services/walletApi'

const PAGE_SIZE = 25

const COLUMNS = [
  { key: 'customer', label: 'Customer' },
  { key: 'balance', label: 'Balance', align: 'right' as const },
  { key: 'cash', label: 'Customer-funded', align: 'right' as const },
  { key: 'promo', label: 'Promotional', align: 'right' as const },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: '' },
]

function AdjustModal({
  customer,
  onClose,
  onDone,
}: {
  customer: WalletCustomerRecord
  onClose: () => void
  onDone: () => void
}) {
  const colors = useColors()
  const [direction, setDirection] = useState<'credit' | 'debit'>('credit')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const parsedAmount = Number(amount)
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0
  // The server rejects a short reason too; mirroring it here means the operator
  // finds out before they submit rather than after.
  const reasonValid = reason.trim().length >= 3
  const wouldOverdraw = direction === 'debit' && amountValid && parsedAmount > customer.balance

  const balanceAfter = direction === 'credit'
    ? customer.balance + (amountValid ? parsedAmount : 0)
    : customer.balance - (amountValid ? parsedAmount : 0)

  const submit = async () => {
    if (!amountValid || !reasonValid || wouldOverdraw || saving) return
    setSaving(true)
    try {
      await adjustWallet({
        user_id: customer.user_id,
        amount: parsedAmount,
        direction,
        reason: reason.trim(),
      })
      emitToast(
        `${direction === 'credit' ? 'Credited' : 'Debited'} ${formatINR(parsedAmount)}. ${customer.name || customer.phone}`,
        'success',
      )
      onDone()
      onClose()
    } catch {
      // The shared axios instance already toasts the server's message.
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={`Adjust wallet. ${customer.name || customer.phone}`}
      onClose={onClose}
      width="460px"
      footer={
        <>
          <PillButton variant="secondary" onClick={onClose} disabled={saving}>Cancel</PillButton>
          <PillButton onClick={submit} disabled={!amountValid || !reasonValid || wouldOverdraw || saving}>
            {saving ? 'Posting…' : `Post ${direction}`}
          </PillButton>
        </>
      }
    >
      <div style={{ display: 'grid', gap: '14px' }}>
        <Field label="Direction">
          <SegmentedControl<'credit' | 'debit'>
            value={direction}
            options={[{ value: 'credit', label: 'Credit' }, { value: 'debit', label: 'Debit' }]}
            onChange={setDirection}
          />
        </Field>

        <Field
          label="Amount (₹)"
          error={wouldOverdraw ? `Only ${formatINR(customer.balance)} available. A wallet cannot go negative.` : undefined}
        >
          <TextInput
            type="number"
            min="0"
            step="0.01"
            value={amount}
            invalid={wouldOverdraw}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0.00"
          />
        </Field>

        <Field
          label="Reason (required)"
          hint="Recorded on the ledger row and shown to the customer."
        >
          <TextArea
            rows={3}
            value={reason}
            invalid={reason.length > 0 && !reasonValid}
            onChange={(event) => setReason(event.target.value)}
            placeholder="e.g. Goodwill credit for the delayed order RD-1042"
          />
        </Field>

        {/* Restate the movement before it happens. §11's before/after, shown to
            the person about to cause it. */}
        <div
          style={{
            background: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            padding: '12px 14px',
            fontSize: '13px',
            color: colors.textSecondary,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Balance before</span>
            <strong>{formatINR(customer.balance)}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
            <span>Balance after</span>
            <strong style={{ color: wouldOverdraw ? '#991B1B' : colors.textPrimary }}>
              {formatINR(Math.max(0, balanceAfter))}
            </strong>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default function WalletCustomersTab() {
  const colors = useColors()
  const user = useAuthStore((state) => state.user)
  const canAdjust = userHasPermission(user, 'wallet.adjust')

  const [rows, setRows] = useState<WalletCustomerRecord[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [adjusting, setAdjusting] = useState<WalletCustomerRecord | null>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 350)
    return () => clearTimeout(id)
  }, [search])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const result = await getWalletCustomers({
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      })
      setRows(result.customers)
      setTotal(result.total)
    } catch {
      setError('Could not load wallet balances.')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, page])

  useEffect(() => {
    void load()
  }, [load])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <Card padded={false}>
      <div
        style={{
          display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap',
          padding: '16px', borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Field label="Search">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Name or phone"
            ariaLabel="Search wallet customers"
          />
        </Field>
        <PillButton variant="secondary" onClick={load} disabled={loading}>
          <RefreshCw size={14} /> Refresh
        </PillButton>
      </div>

      {error ? (
        <div style={{ padding: '20px' }}><LoadErrorState message={error} onRetry={load} /></div>
      ) : rows.length === 0 && !loading ? (
        <div style={{ padding: '20px' }}>
          <EmptyState title="No wallets" description="No customer matches this search." />
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
            <TableHead columns={COLUMNS} />
            <tbody>
              {rows.map((row) => (
                <tr key={row.user_id}>
                  <Td>
                    <div>{row.name || '-'}</div>
                    <div style={{ fontSize: '12px', color: colors.textMuted }}>{row.phone}</div>
                  </Td>
                  <Td align="right" strong>{formatINR(row.balance)}</Td>
                  <Td align="right" muted>{formatINR(row.cash_balance)}</Td>
                  <Td align="right" muted>{formatINR(row.promo_balance)}</Td>
                  <Td>
                    {row.status === 'frozen'
                      ? <Chip tone="danger">On hold</Chip>
                      : <Chip tone="success">Active</Chip>}
                  </Td>
                  <Td align="right">
                    {canAdjust ? (
                      <PillButton variant="secondary" onClick={() => setAdjusting(row)}>
                        Adjust
                      </PillButton>
                    ) : null}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 ? (
        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 16px', borderTop: `1px solid ${colors.border}`,
          }}
        >
          <span style={{ fontSize: '13px', color: colors.textMuted }}>
            Page {page} of {totalPages} · {total} wallets
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <PillButton variant="secondary" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
              Previous
            </PillButton>
            <PillButton variant="secondary" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
              Next
            </PillButton>
          </div>
        </div>
      ) : null}

      {adjusting ? (
        <AdjustModal customer={adjusting} onClose={() => setAdjusting(null)} onDone={load} />
      ) : null}
    </Card>
  )
}
